import { useCallback, useMemo } from 'react';
import type { ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AccountBalance,
  CloseFullscreen,
  FactCheck,
  LocalAtm,
  OpenInFull,
  ReceiptLong,
  SyncProblem,
  TaskAlt,
  WarningAmber,
} from '@mui/icons-material';
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FlapDisplay, Presets } from 'split-flap-lib';
import 'split-flap-lib/dist/index.css';

import { BackButton } from '../components/back-button';
import { useRcmScoreboardQuery } from '../store/hooks';
import { formatCents } from '../utils/formatting';

type RcmScoreboardPeriod =
  | 'month_to_date'
  | 'this_month'
  | 'last_30_days'
  | 'last_90_days'
  | 'all_time'
  | 'custom';

const JUNE_TARGET_CENTS = 1_700_000;
const JUNE_START = '2026-06-01';
const JUNE_END = '2026-06-30';
const FLAP_CHARACTERS = `${Presets.ALPHANUM}$%,./:-+`;

const periodOptions: Array<{ value: RcmScoreboardPeriod; label: string }> = [
  { value: 'month_to_date', label: 'Month to date' },
  { value: 'this_month', label: 'Full month' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'all_time', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

function formatFlapMoney(cents: number | undefined | null) {
  const dollars = Math.round((cents ?? 0) / 100);
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(1)}K`;
  }
  return `$${dollars.toLocaleString('en-US')}`;
}

function formatFlapCount(value: number | undefined | null) {
  return String(value ?? 0);
}

function sanitizeFlapValue(value: string, length: number) {
  const sanitized = value
    .toUpperCase()
    .split('')
    .map(char => (FLAP_CHARACTERS.includes(char) ? char : ' '))
    .join('');
  return sanitized.slice(0, length);
}

function FlipBoardValue({ value, length }: { value: string; length: number }) {
  return (
    <FlapDisplay
      chars={FLAP_CHARACTERS}
      className="rcm-split-flap-display"
      hinge
      length={length}
      padMode="end"
      timing={18}
      value={sanitizeFlapValue(value, length)}
    />
  );
}

function FlipMetric({
  label,
  value,
  length = 6,
  caption,
  fullscreen = false,
}: {
  label: string;
  value: string;
  length?: number;
  caption?: string;
  fullscreen?: boolean;
}) {
  return (
    <div
      className={
        fullscreen
          ? 'flex min-h-[min(42vh,440px)] flex-col justify-center rounded-md border border-zinc-700 bg-zinc-950 p-7 shadow-sm'
          : 'min-h-[300px] rounded-md border border-zinc-700 bg-zinc-950 p-6 shadow-sm'
      }
    >
      <div
        className={
          fullscreen
            ? 'mb-5 text-base font-semibold uppercase tracking-wide text-zinc-400'
            : 'mb-4 text-sm font-semibold uppercase tracking-wide text-zinc-400'
        }
      >
        {label}
      </div>
      <div className="overflow-hidden">
        <FlipBoardValue value={value} length={length} />
      </div>
      {caption && (
        <div className={fullscreen ? 'mt-5 text-base text-zinc-500' : 'mt-4 text-sm text-zinc-500'}>
          {caption}
        </div>
      )}
    </div>
  );
}

function StatPill({
  icon,
  label,
  value,
  tone,
}: {
  icon: ReactElement;
  label: string;
  value: string;
  tone: string;
}) {
  return (
    <div className="rounded-md border border-gray-200 bg-white px-5 py-5">
      <div className="flex items-center gap-3 text-sm font-medium uppercase text-gray-500">
        <span className={`flex h-9 w-9 items-center justify-center rounded-md ${tone}`}>
          {icon}
        </span>
        {label}
      </div>
      <div className="mt-4 text-3xl font-semibold text-gray-950">{value}</div>
    </div>
  );
}

export default function RcmScoreboard() {
  const [searchParams, setSearchParams] = useSearchParams();

  const period = (searchParams.get('period') || 'month_to_date') as RcmScoreboardPeriod;
  const periodStart = searchParams.get('periodStart') || JUNE_START;
  const periodEnd = searchParams.get('periodEnd') || JUNE_END;
  const isFullscreenMode = searchParams.get('fullscreen') === '1';

  const queryArgs = useMemo(
    () => ({
      period,
      periodStart: period === 'custom' ? periodStart : undefined,
      periodEnd: period === 'custom' ? periodEnd : undefined,
      targetClaimsPaidCents: JUNE_TARGET_CENTS,
    }),
    [period, periodEnd, periodStart],
  );

  const { data, error, refetch } = useRcmScoreboardQuery(queryArgs, {
    pollingInterval: 5 * 60 * 1000,
  });
  const summary = data?.summary;
  const progressPct = Math.min(summary?.claims_paid_progress_pct ?? 0, 100);

  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      const params = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handlePeriodChange = useCallback(
    (newPeriod: RcmScoreboardPeriod) => {
      updateParams({
        period: newPeriod,
        periodStart: newPeriod === 'custom' ? periodStart : undefined,
        periodEnd: newPeriod === 'custom' ? periodEnd : undefined,
      });
    },
    [periodEnd, periodStart, updateParams],
  );

  const enterFullscreenMode = useCallback(() => {
    updateParams({ fullscreen: '1' });
    const fullscreenRequest = document.documentElement.requestFullscreen?.();
    void fullscreenRequest?.catch(() => undefined);
  }, [updateParams]);

  const exitFullscreenMode = useCallback(() => {
    updateParams({ fullscreen: undefined });
    if (document.fullscreenElement) {
      const exitRequest = document.exitFullscreen?.();
      void exitRequest?.catch(() => undefined);
    }
  }, [updateParams]);

  const renderTickerGrid = (fullscreen = false) => (
    <div
      className={
        fullscreen
          ? 'rcm-scoreboard-fullscreen grid w-full grid-cols-1 gap-5 lg:grid-cols-2'
          : 'mb-6 grid grid-cols-1 gap-5 rounded-md bg-zinc-900 p-5 shadow-sm lg:grid-cols-2'
      }
    >
      <FlipMetric
        label="Claims paid"
        value={formatFlapMoney(summary?.insurance_payment_from_era_cents)}
        caption={`Target ${formatCents(summary?.target_claims_paid_cents ?? JUNE_TARGET_CENTS)}`}
        fullscreen={fullscreen}
      />
      <FlipMetric
        label="Progress"
        value={`${(summary?.claims_paid_progress_pct ?? 0).toFixed(1)}%`}
        caption={summary?.period_label ?? 'Month to date'}
        fullscreen={fullscreen}
      />
      <FlipMetric
        label="Submitted"
        value={formatFlapMoney(summary?.submitted_claim_amount_cents)}
        caption={`${String(summary?.claims_submitted_count ?? 0)} claims submitted`}
        fullscreen={fullscreen}
      />
      <FlipMetric
        label="Insurance take"
        value={formatFlapMoney(summary?.insurance_take_cents)}
        caption={`${formatCents(summary?.collected_take_cents ?? 0)} collected · ${formatCents(summary?.invoiced_take_cents ?? 0)} invoiced`}
        fullscreen={fullscreen}
      />
      <FlipMetric
        label="Patient fee take"
        value={formatFlapMoney(summary?.jotpsych_take_cents)}
        caption={`${String(summary?.paid_patient_invoice_count ?? 0)} paid invoices`}
        fullscreen={fullscreen}
      />
    </div>
  );

  if (isFullscreenMode) {
    return (
      <div className="fixed inset-0 z-[2000] bg-zinc-950">
        <main className="relative flex h-screen items-center overflow-hidden px-6 py-6">
          <Tooltip title="Exit ticker mode">
            <IconButton
              aria-label="Exit ticker mode"
              className="!absolute right-5 top-5 !z-10 !border !border-zinc-700 !bg-zinc-900/85 !text-zinc-200 hover:!bg-zinc-800"
              onClick={exitFullscreenMode}
              size="large"
            >
              <CloseFullscreen />
            </IconButton>
          </Tooltip>
          {renderTickerGrid(true)}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="mx-auto max-w-screen-2xl px-6 py-8">
        <BackButton fallbackUrl="/admin-v2/billing-dashboard" />

        <div className="mt-4 mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <AccountBalance className="text-zinc-800" fontSize="large" />
              <Typography variant="h4" className="font-bold text-gray-950">
                June RCM Scoreboard
              </Typography>
            </div>
            <Typography variant="body1" className="mt-1 max-w-4xl text-gray-500">
              Claims paid, payer response, patient payment, and June target progress.
            </Typography>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button startIcon={<OpenInFull />} variant="outlined" onClick={enterFullscreenMode}>
              Ticker mode
            </Button>
            <Button variant="outlined" onClick={() => refetch()}>
              Refresh board
            </Button>
          </div>
        </div>

        {renderTickerGrid()}

        <div className="mb-6 rounded-md border border-gray-200 bg-white p-6">
          <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <div className="text-sm font-semibold uppercase text-gray-500">June target</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-4xl font-bold text-gray-950">
                  {formatCents(summary?.insurance_payment_from_era_cents)}
                </span>
                <span className="text-base text-gray-500">
                  of {formatCents(summary?.target_claims_paid_cents ?? JUNE_TARGET_CENTS)}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  label="Period"
                  value={period}
                  onChange={event => handlePeriodChange(event.target.value as RcmScoreboardPeriod)}
                >
                  {periodOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                disabled={period !== 'custom'}
                label="Start"
                size="small"
                type="date"
                value={periodStart}
                onChange={event =>
                  updateParams({ periodStart: event.target.value, period: 'custom' })
                }
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                disabled={period !== 'custom'}
                label="End"
                size="small"
                type="date"
                value={periodEnd}
                onChange={event =>
                  updateParams({ periodEnd: event.target.value, period: 'custom' })
                }
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <Button
                variant="contained"
                onClick={() =>
                  updateParams({
                    period: 'custom',
                    periodStart: JUNE_START,
                    periodEnd: JUNE_END,
                  })
                }
              >
                June 2026
              </Button>
            </div>
          </div>
          <LinearProgress
            aria-label="Claims paid progress"
            variant="determinate"
            value={progressPct}
            sx={{ height: 16, borderRadius: 1 }}
          />
          <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
            <span>
              {summary?.period_start || 'Beginning'} to {summary?.period_end || 'today'}
            </span>
            <span>{(summary?.claims_paid_progress_pct ?? 0).toFixed(1)}%</span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
          <StatPill
            icon={<TaskAlt fontSize="small" />}
            label="Clean"
            value={formatFlapCount(summary?.clean_outcome_count)}
            tone="bg-emerald-50 text-emerald-700"
          />
          <StatPill
            icon={<WarningAmber fontSize="small" />}
            label="Denials"
            value={formatFlapCount(summary?.era_denial_or_reversal_count)}
            tone="bg-rose-50 text-rose-700"
          />
          <StatPill
            icon={<FactCheck fontSize="small" />}
            label="Responses"
            value={formatFlapCount(summary?.claims_with_payer_response_count)}
            tone="bg-blue-50 text-blue-700"
          />
          <StatPill
            icon={<SyncProblem fontSize="small" />}
            label="Missing"
            value={formatFlapCount(summary?.claims_missing_payer_response_count)}
            tone="bg-amber-50 text-amber-700"
          />
          <StatPill
            icon={<LocalAtm fontSize="small" />}
            label="Patient pay"
            value={formatCents(summary?.patient_payment_allocated_cents)}
            tone="bg-violet-50 text-violet-700"
          />
          <StatPill
            icon={<ReceiptLong fontSize="small" />}
            label="Needs attn"
            value={formatFlapCount(summary?.claims_needing_attention_count)}
            tone="bg-slate-100 text-slate-700"
          />
        </div>

        {error && (
          <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Scoreboard data is not available yet. Check that the migration has been applied and the
            API has restarted.
          </div>
        )}
      </main>
    </div>
  );
}
